'use client'

import { PackageOpen } from 'lucide-react'
import { DateTime } from 'luxon'

import { useRewardRequestsHistory } from '../hooks/useRewardRequests'
import { resolveRewardRequestState } from '../lib/rewardRequestStatus'

import { RewardRequestBadge } from './RewardRequestBadge'

import type { RewardRequest } from '@/features/store/types/reward'
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  CardTile
} from '@/shared/ui/Card'

const formatDate = (iso: string) =>
  DateTime.fromISO(iso).setLocale('pt-BR').toLocaleString(DateTime.DATE_MED)

const RedemptionRow = ({ request }: { request: RewardRequest }) => {
  const state = resolveRewardRequestState(request)
  const reward = request.reward_item?.reward
  const imageUrl =
    reward?.image?.file?.url ||
    request.reward_item?.image?.file?.url ||
    '/misc/pack.png'

  return (
    <CardTile>
      <img
        src={imageUrl}
        alt={reward?.name || 'Recompensa'}
        className="size-15 rounded-lg object-cover"
      />
      <div className="flex flex-col gap-1 min-w-0">
        <span className="text-lg font-bold text-primary-dark font-sans">
          {reward?.name || 'Recompensa'}
        </span>
        <span className="text-sm text-gray-600">
          {request.quantity}x {request.reward_item?.name}
        </span>
        <span className="text-xs text-gray-400">
          {formatDate(request.created_at)}
        </span>
      </div>
      <div className="ml-auto flex-shrink-0">
        {state !== 'none' && <RewardRequestBadge state={state} />}
      </div>
    </CardTile>
  )
}

export const StoreHistoryList = () => {
  const { requests, isLoading, isEmpty } = useRewardRequestsHistory()

  return (
    <div className="flex flex-col gap-4 w-full items-center">
      <Card>
        <CardHeader>
          <CardTitle>Meus resgates</CardTitle>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className="flex flex-col gap-3">
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className="h-20 w-full animate-pulse rounded-xl bg-gray-200/60"
                />
              ))}
            </div>
          ) : isEmpty ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <PackageOpen className="text-gray-400" size={40} />
              <span className="text-lg font-bold text-primary-dark">
                Você ainda não resgatou nada
              </span>
              <span className="text-sm text-gray-500">
                Assim que você resgatar uma recompensa na lojinha, ela aparece
                aqui.
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {requests.map(request => (
                <RedemptionRow key={request.id} request={request} />
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}
